import {
  Identify,
  identify as amplitudeIdentify,
  Revenue,
  revenue as amplitudeRevenue,
  setUserId as amplitudeSetUserId,
  track as amplitudeTrack,
  flush as amplitudeFlush,
} from '@amplitude/analytics-browser';
import {
  AMPLITUDE_DROP_LIST,
  type RevenueProps,
  type UserTraits,
} from '@src/Tracking/catalog';
import type {
  TrackingAdapter,
  TrackingEventName,
  TrackingPayload,
} from '@src/Providers/Tracking/TrackingContext';
import {getVendorConsent} from '@src/Providers/DidomiInitProvider/isTrackingEnabled';

/**
 * Amplitude browser SDK adapter.
 * - Drops high-volume events (player_tick, feed_heartbeat) so Amplitude
 *   ingest costs stay bounded.
 * - Emits a single canonical snake_case name per event (no alias dual-emit).
 *   Legacy dot-notation continuity is handled at the analytics layer
 *   (Amplitude Transformations), not by sending a second event here.
 * - Consent-gated via Didomi (amplitude purpose).
 */
export const createAmplitudeAdapter = (): TrackingAdapter => ({
  name: 'amplitude',
  isEnabled: () => getVendorConsent().amplitude,

  track: (event: TrackingEventName, props?: TrackingPayload) => {
    if (typeof event !== 'string') return;
    if (AMPLITUDE_DROP_LIST.has(event)) return;

    try {
      // Single canonical event only — no alias dual-emit. Legacy dot-notation
      // continuity is handled at the analytics layer (Amplitude Transformations),
      // driven by the events.registry `replaces` map, not by sending a second event.
      amplitudeTrack(event, props);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[amplitude] track failed', {event, err});
    }
  },

  identify: (userId: string, traits?: UserTraits) => {
    try {
      amplitudeSetUserId(userId);
      if (traits && Object.keys(traits).length > 0) {
        const id = new Identify();
        Object.entries(traits).forEach(([k, v]) => {
          if (v !== undefined && v !== null) id.set(k, v as never);
        });
        amplitudeIdentify(id);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[amplitude] identify failed', err);
    }
  },

  trackRevenue: (rev: RevenueProps) => {
    try {
      const r = new Revenue()
        .setProductId(rev.product_id)
        .setPrice(rev.price)
        .setQuantity(rev.quantity ?? 1);
      if (rev.revenue_type) r.setRevenueType(rev.revenue_type);
      if (rev.receipt) r.setReceipt(rev.receipt);
      if (rev.currency) {
        r.setEventProperties({currency: rev.currency});
      }
      amplitudeRevenue(r);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[amplitude] revenue failed', err);
    }
  },

  // Force any queued events to send now. Used before navigating away (e.g. the
  // Stripe redirect) so events are not lost when the page unloads.
  flush: () => {
    try {
      return amplitudeFlush().promise;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[amplitude] flush failed', err);
    }
  },
});
